import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Hidden,
  Typography,
  Stack,
} from "@mui/material";
import finance from "financejs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  numberFormat,
  participationsFormat,
} from "../../../helpers/numberFormat";
import { DetailLatentStartup } from "./DetailLatentStartup";
import { transformInvestments } from "../../../helpers/finance";

export const PositionTableSmall = ({ rows }) => {
  const { t } = useTranslation("global");
  const startupList = rows.map((alias) => alias.alias);
  const startupListNoRepeat = startupList.filter((item, index) => {
    return startupList.indexOf(item) === index;
  });
  const objByStartup = startupListNoRepeat.map((startup) => {
    return {
      id: startup,
      startup,
      data: rows.filter((investment) => investment.alias === startup),
    };
  });

  const capitalGain = (invest) => {
    const res = invest.data.map((invest) => {
      const sharesAmount = invest.participationValuation
        ? invest.amount / invest.participationValuation
        : 0;
      const newCapital = sharesAmount * invest.newAmount;
      const resp = invest.participationValuation
        ? newCapital - invest.amount
        : 0;
      return invest.investmentType === "Préstamo Convertible" ? 0 : resp;
    });
    return numberFormat(res.reduce((a, b) => a + b, 0));
  };

  return objByStartup.map((invest) => {
    const amount = invest?.data?.reduce(
      (acc, current) => acc + current.amount,
      0
    );
    const participationValuation = () => {
      const res = invest.data.map((invest) => {
        const sharesAmount = invest.participationValuation
          ? invest.amount / invest.participationValuation
          : 0;
        const newCapital = sharesAmount * invest.newAmount;
        const resp = invest.participationValuation
          ? newCapital - invest.amount
          : 0;
        return invest.investmentType === "Préstamo Convertible" ? 0 : resp;
      });
      return res.reduce((a, b) => a + b, 0);
    };

    const moic = participationsFormat(
      (amount + participationValuation()) / amount
    );

    const tir = () => {
      const investments = invest.data.map((inv) => {
        const sharesAmount = inv.participationValuation
          ? inv.amount / inv.participationValuation
          : 0;
        const newCapital = sharesAmount * inv.newAmount;
        const resp = inv.participationValuation ? newCapital - inv.amount : 0;
        const capitalGain =
          inv.investmentType === "Préstamo Convertible"
            ? 0
            : parseInt(resp.toFixed(2));

        return {
          cashflow: inv.amount,
          investmentDate: inv.investmentDate,
          capitalGain: capitalGain === -0 ? 0 : capitalGain,
        };
      });

      const investmentsFinal = transformInvestments(investments);
      const financeInstance = new finance();
      
      try {
      const tir = financeInstance.XIRR(
          investmentsFinal.map((inv) => inv.cashflow),
          investmentsFinal.map((inv) => new Date(inv.investmentDate)),
          -0.25
        )
        return isNaN(tir) || tir === Infinity
          ? "0,00%"
          : participationsFormat(tir.toFixed(2));
      } catch (error) {
        console.error("Error al calcular la TIR:", error);
        return "-";
      }
    };

    return (
      <Hidden mdUp key={invest.id}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Stack width="100%" direction="row" gap={1}>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  md={{ justifyContent: "space-between" }}
                >
                  <Typography
                    variant="caption"
                    color="primary"
                    marginRight="10px"
                  >
                    {t("positions.startup")}:{" "}
                  </Typography>
                  <Typography variant="caption" color="secondary">
                    {invest.startup}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  md={{ justifyContent: "space-between" }}
                >
                  <Typography
                    variant="caption"
                    color="primary"
                    marginRight="10px"
                  >
                    {t("positions.url")}:
                  </Typography>
                  <a
                    href={invest.data[0].url}
                    style={{ color: "#EA7F22", fontSize: "12px" }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("link.link")}
                  </a>
                </Stack>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  md={{ justifyContent: "space-between" }}
                >
                  <Typography
                    variant="caption"
                    color="primary"
                    marginRight="10px"
                  >
                    {t("positions.amount")}:
                  </Typography>
                  <Typography
                    variant="caption"
                    color={
                      invest.myStatus === "Reserva realizada"
                        ? "success.main"
                        : "secondary"
                    }
                  >
                    {numberFormat(
                      invest.data
                        .map((invest) => invest.amount)
                        .reduce((a, b) => a + b, 0)
                    )}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  md={{ justifyContent: "space-between" }}
                >
                  <Typography
                    variant="caption"
                    color="primary"
                    marginRight="10px"
                  >
                    {t("positions.capitalGain")}:
                  </Typography>
                  <Typography
                    variant="caption"
                    color={
                      invest.myStatus === "Reserva realizada"
                        ? "success.main"
                        : "secondary"
                    }
                  >
                    {capitalGain(invest)}
                  </Typography>
                </Stack>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Stack
                    direction="row"
                    md={{ justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="caption"
                      color="primary"
                      marginRight="10px"
                    >
                      {t("positions.tir")}:
                    </Typography>
                    <Typography variant="caption" color={"secondary"}>
                      {tir()}%
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    md={{ justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="caption"
                      color="primary"
                      marginRight="10px"
                    >
                      {t("positions.moic")}:
                    </Typography>
                    <Typography variant="caption" color={"secondary"}>
                      {moic}x
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {invest.data.map((detail) => {
              return <DetailLatentStartup row={detail} key={detail.id} />;
            })}
          </AccordionDetails>
        </Accordion>
      </Hidden>
    );
  });
};
